import React, { useState, useMemo } from 'react';
import { useCustomers, useBranches, useLookupMutations } from '../db/hooks/useLookupData';
import { useDatabase } from '../db/DatabaseProvider';
import { useAuthStore } from '../stores';
import { CustomSelect } from '../components/common/CustomSelect';
import { Drawer } from '../components/common/Drawer';
import { DataTable, SyncStateIcon, type ColumnDef } from '../components/common/DataTable';
import { Button } from '../components/common/Button';
import { now } from '../lib/uuid';
import { getValidBranchId } from '../lib/utils';
import type { CustomerDocType } from '../db/schemas';
import { CustomerProfileDrawer } from '../components/customers/CustomerProfileDrawer';
import {
  UserPlus,
  Edit2,
  Trash2,
  Phone,
  Mail,
  AtSign,
  Building2,
  FileText,
  UserCheck,
  Users as UsersIcon,
  Filter,
  Package,
} from 'lucide-react';


import { useToast } from '../components/common/ToastProvider';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { useDataTable } from '../hooks/useDataTable';

export function Customers() {
  const db = useDatabase();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
  const isOwner = useAuthStore((s) => s.isOwner);

  // Fetch ALL non-deleted clients globally across the company
  const { customers, isLoading: loadingCustomers } = useCustomers('all');
  const { branches } = useBranches();
  const { addCustomer } = useLookupMutations();

  // Frontend Filter Controls: Sede Origen vs Última Visita + Branch Selector
  const [filterBranchMode, setFilterBranchMode] = useState<'origen' | 'ultima'>('origen');
  const [filterBranchId, setFilterBranchId] = useState<string>('all');

  // Filter clients dynamically in frontend without restricting global DB query
  const filteredCustomers = useMemo(() => {
    if (filterBranchId === 'all') return customers;
    return customers.filter((c) => {
      const targetId = filterBranchMode === 'ultima'
        ? (c.last_visited_branch_id || c.branch_id)
        : c.branch_id;
      return targetId === filterBranchId;
    });
  }, [customers, filterBranchId, filterBranchMode]);

  // Use generic data table hook for filtering, sorting and pagination
  const tableState = useDataTable({
    data: filteredCustomers,
    searchFields: ['name', 'phone', 'email', 'instagram', 'notes'],
    initialSortBy: 'name',
    sortOptions: [
      { value: 'name', label: 'Nombre' },
      { value: 'phone', label: 'Contacto' },
      { value: 'branch_id', label: 'Sede' },
      { value: 'created_at', label: 'Fecha' },
    ],
    initialPageSize: '25',
    storageKey: 'depilaway-customers-page-size',
  });

  // Optimized O(1) Branch Lookup Map
  const branchesMap = useMemo(() => new Map(branches.map((s) => [s.id, s])), [branches]);

  // Drawer Form State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setNombre] = useState('');
  const [phone, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [instagram, setInstagram] = useState('');
  const [branchId, setBranchId] = useState('');
  const [notes, setNotas] = useState('');

  // Profile Drawer State
  const [selectedClientForProfile, setSelectedClientForProfile] = useState<CustomerDocType | null>(null);
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [profileInitialTab, setProfileInitialTab] = useState<string>('resumen');

  const openProfileDrawer = (client: CustomerDocType, initialTab: string = 'resumen') => {
    setSelectedClientForProfile(client);
    setProfileInitialTab(initialTab);
    setIsProfileDrawerOpen(true);
  };


  const openNew = () => {
    setEditingId(null);
    setNombre('');
    setTelefono('');
    setEmail('');
    setInstagram('');
    setBranchId(selectedBranchId || (branches.length > 0 ? branches[0].id : ''));
    setNotas('');
    setIsFormOpen(true);
  };

  const openEdit = (client: CustomerDocType) => {
    setEditingId(client.id);
    setNombre(client.name || '');
    setTelefono(client.phone || '');
    setEmail(client.email || '');
    setInstagram(client.instagram || '');
    setBranchId(client.branch_id || '');
    setNotas(client.notes || '');
    setIsFormOpen(true);
  };

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });

  const handleDelete = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Cliente',
      message: '¿Estás seguro de que deseas eliminar este cliente del sistema?',
      onConfirm: async () => {
        try {
          const doc = await db.customers.findOne(id).exec();
          if (doc) {
            await doc.patch({
              is_deleted: true,
              updated_at: now(),
              last_modified_by: user?.email || 'system',
              
            });
          }
          toast.success('Cliente eliminado correctamente');
        } catch (err: any) {
          toast.error('Error al eliminar cliente: ' + (err.message || 'Error desconocido'));
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const validBranchId = getValidBranchId(branchId, selectedBranchId, branches);

      if (!validBranchId) {
        toast.error('Debe existir al menos una sucursal registrada para asignar al cliente.', 'Sin Sucursal Válida');
        return;
      }

      if (editingId) {
        const doc = await db.customers.findOne(editingId).exec();
        if (doc) {
          await doc.patch({
            name,
            phone: phone || null,
            email: email || null,
            instagram: instagram || null,
            branch_id: validBranchId,
            notes: notes || null,
            updated_at: now(),
            last_modified_by: user?.email || 'system',
            
          });
        }
        toast.success('Cliente actualizado correctamente');
      } else {
        await addCustomer(name, validBranchId, phone, email, instagram, notes);
        toast.success('Cliente guardado exitosamente');
      }
      setIsFormOpen(false);
    } catch (err: any) {
      console.error('[Clientes] Error al guardar cliente:', err);
      toast.error('Error al guardar cliente: ' + (err.message || 'Error desconocido'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Columns definition for Desktop Table View
  const columns: ColumnDef<CustomerDocType>[] = [
    {
      key: 'name',
      header: 'Cliente',
      render: (client) => (
        <div>
          <div className="font-bold text-text-primary">{client.name}</div>
          {client.instagram && (
            <div className="flex items-center gap-1 text-xs text-brand-sky font-semibold mt-0.5">
              <AtSign className="h-3 w-3" />
              <span>{client.instagram}</span>
            </div>
          )}
          {client.notes && (
            <div className="flex items-center gap-1 text-xs text-text-tertiary mt-1 truncate max-w-xs" title={client.notes}>
              <FileText className="h-3 w-3 shrink-0" />
              <span className="truncate">{client.notes}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'contacto',
      header: 'Contacto',
      render: (client) => (
        <div className="space-y-1 text-text-secondary text-xs">
          {client.phone && (
            <div className="flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5 text-text-tertiary" />
              <span>{client.phone}</span>
            </div>
          )}
          {client.email && (
            <div className="flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-text-tertiary" />
              <span className="truncate max-w-[180px]">{client.email}</span>
            </div>
          )}
          {!client.phone && !client.email && (
            <span className="text-text-tertiary italic">Sin contacto</span>
          )}
        </div>
      ),
    },
    {
      key: 'branch_id',
      header: filterBranchMode === 'origen' ? 'Sede de Origen' : 'Última Visita',
      sortable: true,
      render: (client) => {
        const targetId = filterBranchMode === 'ultima'
          ? (client.last_visited_branch_id || client.branch_id)
          : client.branch_id;
        const branch = branchesMap.get(targetId);
        const isUltima = filterBranchMode === 'ultima' && Boolean(client.last_visited_branch_id);

        return (
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
            isUltima
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
              : 'bg-brand-sky/10 text-brand-sky border border-brand-sky/20'
          }`}>
            <Building2 className="h-3.5 w-3.5" />
            <span>{branch ? branch.name : 'Sede Global'}</span>
            {isUltima && <span className="text-[9px] font-extrabold uppercase ml-0.5">(Atendido)</span>}
          </span>
        );
      },
    },
    {
      key: 'acciones',
      header: 'Acciones',
      sortable: false,
      align: 'right',
      render: (client) => (
        <div className="flex justify-end gap-1">
          <Button variant="outline" size="xs" icon={UserCheck} onClick={() => openProfileDrawer(client, 'resumen')} title="Ver Perfil" aria-label="Ver Perfil" />
          <Button variant="outline" size="xs" icon={Package} onClick={() => openProfileDrawer(client, 'packages')} title="Ver Paquetes & Atenciones" aria-label="Ver packages" />
          <Button variant="ghost" size="xs" icon={Edit2} onClick={() => openEdit(client)} aria-label="Editar cliente" />
          <Button variant="danger" size="xs" icon={Trash2} onClick={() => handleDelete(client.id)} aria-label="Eliminar cliente" />
        </div>
      ),
    },
  ];

  // Rich Custom Render for Mobile Cards (<768px)
  const renderMobileCard = (client: CustomerDocType) => {
    const targetId = filterBranchMode === 'ultima'
      ? (client.last_visited_branch_id || client.branch_id)
      : client.branch_id;
    const branch = branchesMap.get(targetId);
    const cleanPhone = client.phone?.replace(/\D/g, '');
    const isUltima = filterBranchMode === 'ultima' && Boolean(client.last_visited_branch_id);

    return (
      <div className="p-4 rounded-2xl border border-border bg-surface shadow-xs space-y-3 hover:border-brand-orange/40 transition-all">
        {/* Top Header: Name + Branch + Sync Icon */}
        <div className="flex items-start justify-between gap-2 border-b border-border/40 pb-2.5">
          <div>
            <h3 className="font-bold text-base text-text-primary leading-snug">{client.name}</h3>
            {client.instagram && (
              <div className="flex items-center gap-1 text-xs text-brand-sky font-semibold mt-0.5">
                <AtSign className="h-3 w-3" />
                <span>{client.instagram}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <SyncStateIcon state={(client as any).sync_state} />
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
              isUltima
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                : 'bg-brand-sky/10 text-brand-sky border border-brand-sky/20'
            }`}>
              <Building2 className="h-3 w-3" />
              <span>{branch ? branch.name : 'Sede Global'}</span>
            </span>
          </div>
        </div>

        {/* Quick Action Contact Row */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {cleanPhone && (
            <Button
              as="a"
              href={`https://wa.me/${cleanPhone}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="success"
              size="sm"
              icon={Phone}
            >
              {client.phone}
            </Button>
          )}
          {client.email && (
            <Button
              as="a"
              href={`mailto:${client.email}`}
              variant="secondary"
              size="sm"
              icon={Mail}
              className="truncate max-w-[200px]"
            >
              {client.email}
            </Button>
          )}
        </div>

        {/* Notes / Details */}
        {client.notes && (
          <div className="p-2.5 rounded-xl bg-surface-tertiary/50 border border-border/30 text-xs text-text-secondary">
            <div className="flex items-center gap-1 font-semibold text-text-primary mb-0.5">
              <FileText className="h-3 w-3 text-text-tertiary" />
              <span>Notas / Observaciones:</span>
            </div>
            <p className="whitespace-pre-line leading-relaxed">{client.notes}</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 pt-2 border-t border-border/30">
          <Button variant="outline" size="xs" icon={UserCheck} onClick={() => openProfileDrawer(client, 'resumen')} className="!px-2 !py-1.5 !text-xs font-bold justify-center">
            Perfil
          </Button>
          <Button variant="outline" size="xs" icon={Package} onClick={() => openProfileDrawer(client, 'packages')} className="!px-2 !py-1.5 !text-xs font-bold justify-center">
            Paquetes
          </Button>
          <Button variant="secondary" size="xs" icon={Edit2} onClick={() => openEdit(client)} className="!px-2 !py-1.5 !text-xs font-bold justify-center">
            Editar
          </Button>
          <Button variant="danger" size="xs" icon={Trash2} onClick={() => handleDelete(client.id)} className="!px-2 !py-1.5 !text-xs font-bold justify-center">
            Eliminar
          </Button>
        </div>
      </div>
    );
  };


  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-text-primary tracking-tight">Directorio de Clientes</h1>
          <p className="mt-1 text-sm text-text-secondary leading-relaxed">
            Directorio global unificado de customers y seguimiento de visitas multisede
          </p>
        </div>
        <Button variant="primary" size="md" icon={UserPlus} onClick={openNew} className="self-start sm:self-auto">
          Nuevo Cliente
        </Button>
      </div>

      {/* Unified DataTable Component with Single Integrated Toolbar */}
      <DataTable<CustomerDocType>
        data={filteredCustomers}
        columns={columns}
        getItemKey={(item) => item.id}
        getSyncState={(item) => (item as any).sync_state}
        tableState={tableState}
        isLoading={loadingCustomers}
        renderMobileCard={renderMobileCard}
        extraToolbar={
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {/* Toggle Mode Control: Sede Origen vs Última Visita */}
            <div className="flex items-center gap-1 p-1 bg-surface-secondary/80 rounded-xl border border-border shrink-0">
              <button
                type="button"
                onClick={() => setFilterBranchMode('origen')}
                className={`px-2.5 sm:px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                  filterBranchMode === 'origen'
                    ? 'bg-surface text-brand-orange shadow-xs border border-border'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                Sede Origen
              </button>
              <button
                type="button"
                onClick={() => setFilterBranchMode('ultima')}
                className={`px-2.5 sm:px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                  filterBranchMode === 'ultima'
                    ? 'bg-surface text-brand-orange shadow-xs border border-border'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                Última Visita
              </button>
            </div>

            {/* Branch Filter Selector */}
            <div className="flex items-center gap-2 flex-1 min-w-[180px] max-w-full sm:max-w-[240px]">
              <Filter className="h-4 w-4 text-text-tertiary shrink-0" />
              <div className="flex-1 min-w-[140px]">
                <CustomSelect
                  value={filterBranchId}
                  onChange={setFilterBranchId}
                  options={[
                    { value: 'all', label: 'Todas las sedes' },
                    ...branches.map((s) => ({ value: s.id, label: s.name })),
                  ]}
                  buttonClassName="!h-10 !py-1.5 !text-xs"
                />
              </div>
            </div>
          </div>
        }
        searchPlaceholder="Buscar por name, teléfono, correo o notes..."
        emptyTitle="Sin customers registrados"
        emptyDescription="Comienza agregando tu primer cliente a la base de datos."
        emptyIcon={UsersIcon}
        actionButton={
          <Button variant="primary" size="sm" icon={UserPlus} onClick={openNew}>
            Registrar Primer Cliente
          </Button>
        }
      />

      {/* Reusable Drawer for Add/Edit Client Form */}
      <Drawer
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={editingId ? 'Editar Cliente' : 'Registrar Nuevo Cliente'}
        subtitle="Ingresa la información básica y datos de contacto del cliente"
        icon={UserCheck}
        maxWidth="lg"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsFormOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              form="cliente-form"
              isLoading={isSubmitting}
            >
              {editingId ? 'Actualizar Cliente' : 'Guardar Cliente'}
            </Button>
          </>
        }
      >
        <form id="cliente-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Nombre Completo *</label>
            <input
              required
              type="text"
              value={name}
              onChange={(e) => setNombre(e.target.value)}
              className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              placeholder="Ej. María Pérez"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Teléfono</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setTelefono(e.target.value)}
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
                placeholder="Ej. +58 412-1234567"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Correo Electrónico</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
                placeholder="Ej. maria@correo.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Instagram</label>
              <input
                type="text"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
                placeholder="Ej. @mariaperez"
              />
            </div>
            {isOwner && (
              <div>
                <label className="block text-xs font-semibold text-text-secondary mb-1">Sucursal Asociada *</label>
                <CustomSelect
                  value={branchId}
                  onChange={setBranchId}
                  options={branches.map((s) => ({ value: s.id, label: s.name }))}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Notas / Observaciones</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotas(e.target.value)}
              className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              placeholder="Preferencias del cliente, type de piel, contraindicaciones..."
            />
          </div>
        </form>
      </Drawer>

      {/* Profile Drawer */}
      <CustomerProfileDrawer
        isOpen={isProfileDrawerOpen}
        onClose={() => setIsProfileDrawerOpen(false)}
        customer={selectedClientForProfile}
        initialTab={profileInitialTab}
      />

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="danger"
        confirmText="Eliminar"
      />
    </div>
  );
}

